import React, { useState } from 'react';
import { Plus, Building, Users, Settings, Trash2, Edit, Mail, UserPlus, FileText } from 'lucide-react';
import {
  useGetTenantsQuery,
  useCreateTenantMutation,
  useUpdateTenantMutation,
  useDeleteTenantMutation,
  useGetTenantUsersQuery,
  useInviteUserToTenantMutation,
  useDeleteUserFromTenantMutation,
  useAssignUserClaimMutation,
  useRemoveUserClaimMutation,
  useGetUserClaimsQuery,
  useGetCurrentUserQuery,
  Tenant,
  User,
  UserInvite,
  UserClaim,
} from '../../store/api/apiSlice';
import { useSelector } from 'react-redux';

interface TenantFormData {
  name: string;
  company_name: string;
  domain_name: string;
  seats: number;
  document_quota: number;
  slug: string;
  description?: string;
}

const TenantForm: React.FC<{
  tenant?: Tenant;
  onSubmit: (data: TenantFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}> = ({ tenant, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState<TenantFormData>({
    name: tenant?.name || '',
    company_name: tenant?.company_name || '',
    domain_name: tenant?.domain_name || '',
    seats: tenant?.seats || 10,
    document_quota: tenant?.document_quota || 100,
    slug: tenant?.slug || '',
    description: tenant?.description || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      ...(tenant ? {} : { slug: generateSlug(name) })
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Company Name
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleNameChange(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Internal Company Name
        </label>
        <input
          type="text"
          value={formData.company_name}
          onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Domain Name
        </label>
        <input
          type="text"
          value={formData.domain_name}
          onChange={(e) => setFormData(prev => ({ ...prev, domain_name: e.target.value }))}
          placeholder="example.com"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          required
        />
        <p className="mt-1 text-sm text-gray-500">
          Users with email addresses ending in this domain will be automatically assigned to this tenant.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Number of Seats
        </label>
        <input
          type="number"
          value={formData.seats}
          onChange={(e) => setFormData(prev => ({ ...prev, seats: parseInt(e.target.value) || 1 }))}
          min="1"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Document Quota
        </label>
        <input
          type="number"
          value={formData.document_quota}
          onChange={(e) => setFormData(prev => ({ ...prev, document_quota: parseInt(e.target.value) || 1 }))}
          min="1"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          required
        />
        <p className="mt-1 text-sm text-gray-500">
          Maximum number of documents this tenant can upload.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Slug
        </label>
        <input
          type="text"
          value={formData.slug}
          onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          pattern="^[a-z0-9-]+$"
          required
          disabled={!!tenant}
        />
        <p className="mt-1 text-sm text-gray-500">
          URL-friendly identifier (lowercase letters, numbers, and hyphens only).
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
        />
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : tenant ? 'Update Tenant' : 'Create Tenant'}
        </button>
      </div>
    </form>
  );
};

const UserInviteForm: React.FC<{
  onSubmit: (data: UserInvite) => void;
  onCancel: () => void;
  isLoading?: boolean;
}> = ({ onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState<UserInvite>({
    email: '',
    name: '',
    role: 'user',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Email Address
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Name (Optional)
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Role
        </label>
        <select
          value={formData.role}
          onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'admin' | 'user' | 'viewer' }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
        >
          <option value="viewer">Viewer</option>
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Sending...' : 'Send Invitation'}
        </button>
      </div>
    </form>
  );
};

interface ConfirmationDialog {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const TenantManagement: React.FC = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [confirmationDialog, setConfirmationDialog] = useState<ConfirmationDialog>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    onCancel: () => {},
  });

  // Get auth state to determine when API calls should be made
  const auth = useSelector((state: any) => state.auth);
  const isAuthReady = auth.isAuthenticated && !auth.isLoading && auth.token;

  const { data: currentUser } = useGetCurrentUserQuery(undefined, {
    // Skip API call until authentication is ready
    skip: !isAuthReady
  });
  const { data: tenantsData, isLoading: loadingTenants } = useGetTenantsQuery({}, {
    // Skip API call until authentication is ready
    skip: !isAuthReady
  });
  const [createTenant, { isLoading: creatingTenant }] = useCreateTenantMutation();
  const [updateTenant, { isLoading: updatingTenant }] = useUpdateTenantMutation();
  const [deleteTenant] = useDeleteTenantMutation();

  const { data: usersData, isLoading: loadingUsers } = useGetTenantUsersQuery(
    { tenantId: selectedTenant?.id || '' },
    { skip: !selectedTenant || !isAuthReady }
  );
  const [inviteUser, { isLoading: invitingUser }] = useInviteUserToTenantMutation();
  const [deleteUser] = useDeleteUserFromTenantMutation();
  const [assignUserClaim] = useAssignUserClaimMutation();
  const [removeUserClaim] = useRemoveUserClaimMutation();
  
  // Check if current user is superadmin or tenant admin
  const isSuperadmin = currentUser?.claims?.some(claim => claim.claim_type === 'superadmin') || false;
  const isTenantAdmin = selectedTenant && currentUser?.claims?.some(
    claim => claim.claim_type === 'tenant_admin' && claim.resource_id === selectedTenant.id
  ) || false;
  
  const canManageUsers = isSuperadmin || isTenantAdmin;

  const handleCreateTenant = async (data: TenantFormData) => {
    try {
      await createTenant(data).unwrap();
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating tenant:', error);
    }
  };

  const handleUpdateTenant = async (data: TenantFormData) => {
    if (!editingTenant) return;
    try {
      await updateTenant({ id: editingTenant.id, ...data }).unwrap();
      setEditingTenant(null);
    } catch (error) {
      console.error('Error updating tenant:', error);
    }
  };

  const handleDeleteTenant = async (tenant: Tenant) => {
    setConfirmationDialog({
      isOpen: true,
      title: 'Delete Tenant',
      message: `Are you sure you want to delete ${tenant.name}? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await deleteTenant(tenant.id).unwrap();
          setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
        } catch (error) {
          console.error('Error deleting tenant:', error);
          setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
        }
      },
      onCancel: () => setConfirmationDialog(prev => ({ ...prev, isOpen: false })),
    });
  };

  const handleInviteUser = async (userData: UserInvite) => {
    if (!selectedTenant) return;
    try {
      await inviteUser({ tenantId: selectedTenant.id, userData }).unwrap();
      setShowInviteForm(false);
    } catch (error) {
      console.error('Error inviting user:', error);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!selectedTenant) return;
    setConfirmationDialog({
      isOpen: true,
      title: 'Remove User',
      message: `Are you sure you want to remove ${user.email} from ${selectedTenant.name}?`,
      onConfirm: async () => {
        try {
          await deleteUser({ tenantId: selectedTenant.id, userId: user.id }).unwrap();
          setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
        } catch (error) {
          console.error('Error deleting user:', error);
          setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
        }
      },
      onCancel: () => setConfirmationDialog(prev => ({ ...prev, isOpen: false })),
    });
  };

  const handleAssignClaim = async (user: User, claimType: 'tenant_admin' | 'tenant_member') => {
    if (!selectedTenant) return;
    try {
      await assignUserClaim({
        tenantId: selectedTenant.id,
        userId: user.id,
        claimType
      }).unwrap();
    } catch (error) {
      console.error('Error assigning claim:', error);
    }
  };

  const handleRemoveClaim = async (user: User, claimType: 'tenant_admin' | 'tenant_member') => {
    if (!selectedTenant) return;
    try {
      await removeUserClaim({
        tenantId: selectedTenant.id,
        userId: user.id,
        claimType
      }).unwrap();
    } catch (error) {
      console.error('Error removing claim:', error);
    }
  };

  const getUserClaimType = (user: User): 'tenant_admin' | 'tenant_member' | 'none' => {
    if (!user.claims || !selectedTenant) return 'none';
    
    const tenantClaims = user.claims.filter(claim => 
      claim.resource_type === 'tenant' && claim.resource_id === selectedTenant.id
    );
    
    if (tenantClaims.some(claim => claim.claim_type === 'tenant_admin')) {
      return 'tenant_admin';
    } else if (tenantClaims.some(claim => claim.claim_type === 'tenant_member')) {
      return 'tenant_member';
    }
    
    return 'none';
  };

  if (selectedTenant) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => setSelectedTenant(null)}
              className="text-blue-600 hover:text-blue-700 mb-2"
            >
              ← Back to Tenants
            </button>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {selectedTenant.name} - User Management
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {usersData?.total || 0} of {selectedTenant.seats} seats used
            </p>
          </div>
          {canManageUsers && (
            <button
              onClick={() => setShowInviteForm(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invite User
            </button>
          )}
        </div>

        {showInviteForm && (
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Invite User to {selectedTenant.name}
            </h3>
            <UserInviteForm
              onSubmit={handleInviteUser}
              onCancel={() => setShowInviteForm(false)}
              isLoading={invitingUser}
            />
          </div>
        )}

        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Users
          </h3>
          {loadingUsers ? (
            <div className="text-center py-4">Loading users...</div>
          ) : usersData?.items?.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Claims
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {usersData.items.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {user.picture ? (
                              <img
                                className="h-10 w-10 rounded-full"
                                src={user.picture}
                                alt={user.name || user.email}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  {(user.name || user.email)[0].toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {user.name || 'No name'}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getUserClaimType(user) === 'tenant_admin' && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                              Admin
                            </span>
                          )}
                          {getUserClaimType(user) === 'tenant_member' && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              Member
                            </span>
                          )}
                          {getUserClaimType(user) === 'none' && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                              No Access
                            </span>
                          )}
                          {canManageUsers && (
                            <div className="relative">
                              <select
                                value={getUserClaimType(user)}
                                onChange={(e) => {
                                  const newClaimType = e.target.value as 'tenant_admin' | 'tenant_member' | 'none';
                                  const currentClaimType = getUserClaimType(user);
                                  
                                  if (newClaimType === currentClaimType) return;
                                  
                                  // Remove current claim if exists
                                  if (currentClaimType !== 'none') {
                                    handleRemoveClaim(user, currentClaimType);
                                  }
                                  
                                  // Add new claim if not 'none'
                                  if (newClaimType !== 'none') {
                                    handleAssignClaim(user, newClaimType);
                                  }
                                }}
                                className="text-xs bg-transparent border-0 text-gray-600 dark:text-gray-400 cursor-pointer"
                              >
                                <option value="none">No Access</option>
                                <option value="tenant_member">Member</option>
                                <option value="tenant_admin">Admin</option>
                              </select>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.is_active 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {canManageUsers && (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No users in this tenant yet.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Only superadmins can see the full tenant management
  if (!isSuperadmin) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 dark:text-red-400 mb-4">
          Access Denied
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          You don't have permission to access tenant management.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Tenant Management
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Manage organizations and their users
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Tenant
        </button>
      </div>

      {(showCreateForm || editingTenant) && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            {editingTenant ? 'Edit Tenant' : 'Create New Tenant'}
          </h3>
          <TenantForm
            tenant={editingTenant || undefined}
            onSubmit={editingTenant ? handleUpdateTenant : handleCreateTenant}
            onCancel={() => {
              setShowCreateForm(false);
              setEditingTenant(null);
            }}
            isLoading={creatingTenant || updatingTenant}
          />
        </div>
      )}

      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Tenants
        </h3>
        {loadingTenants ? (
          <div className="text-center py-4">Loading tenants...</div>
        ) : tenantsData?.items?.length ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {tenantsData.items.map((tenant) => (
              <div key={tenant.id} className="border rounded-lg p-4 dark:border-gray-700">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <Building className="w-5 h-5 text-gray-400 mr-2" />
                      <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        {tenant.name}
                      </h4>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {tenant.company_name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Domain: {tenant.domain_name}
                    </p>
                    <div className="flex items-center mt-2">
                      <Users className="w-4 h-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {tenant.user_count || 0} / {tenant.seats} users
                      </span>
                    </div>
                    <div className="flex items-center mt-1">
                      <FileText className="w-4 h-4 text-gray-400 mr-1" />
                      <span className={`text-sm ${
                        tenant.documents_used >= tenant.document_quota
                          ? 'text-red-600 dark:text-red-400 font-medium'
                          : tenant.documents_used >= tenant.document_quota * 0.8
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {tenant.documents_used} / {tenant.document_quota} documents
                        {tenant.documents_used >= tenant.document_quota && ' (FULL)'}
                      </span>
                    </div>
                    {tenant.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        {tenant.description}
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setSelectedTenant(tenant)}
                      className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      title="Manage Users"
                    >
                      <Users className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingTenant(tenant)}
                      className="p-2 text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                      title="Edit Tenant"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteTenant(tenant)}
                      className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      title="Delete Tenant"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="mt-3 flex items-center justify-between">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    tenant.is_active 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {tenant.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(tenant.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No tenants found.</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmationDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {confirmationDialog.title}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {confirmationDialog.message}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={confirmationDialog.onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmationDialog.onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 